import React, { useState } from 'react';
import { View } from 'react-native';
import { Text } from '@/components/Text';
import { useStoreActions, useStoreState } from '@/stores/store';
import { updateUserEpa } from '@/api/services/user';
import { writeToEpa } from '@/api/services/epaAuth';
import colors, { appTheme } from '@/config/colors';
import { Divider } from 'react-native-elements';
import { Button } from '@/components/ui/therapist/Button';
import { Modal } from '@/components/ui/therapist/Modal';
import { Icon } from '@/components/Icon';
import { Toggle } from '@/components/ui/therapist/toggle';

import { styles } from './styles';
import { logError } from '@/utils/logHandling';

type DataTransferCardProps = {
  canWriteToEpa: boolean;
};

export const DataTransferCard = ({ canWriteToEpa }: DataTransferCardProps) => {
  const userEpa = useStoreState((state) => state.userEpa);
  const toggleUserAutoUpload = useStoreActions(
    (actions) => actions.setUserIsAutoUploadEpa,
  );

  const [showEpaSuccess, setShowEpaSuccess] = useState<boolean>(false);
  const [showEpaError, setShowEpaError] = useState<boolean>(false);

  const handleWriteToEpa = async () => {
    let writeToEpaSuccess = false;
    try {
      await writeToEpa();
      writeToEpaSuccess = true;
    } catch (error) {
      logError('Failed to write to ePA', error);
    }
    setShowEpaError(!writeToEpaSuccess);
    setShowEpaSuccess(writeToEpaSuccess);
  };

  const handleToggleAutoUpload = async (value: boolean) => {
    try {
      await updateUserEpa({ autoUpload: value });
      toggleUserAutoUpload(value);
    } catch (error) {
      logError('Failed to update user auto upload setting', error);
    }
  };

  return (
    <>
      <View style={[styles.cardContainer, { width: '100%' }]}>
        <View style={[styles.cardSection, styles.headingRow]}>
          <Icon
            name="refresh"
            size={20}
            color={
              canWriteToEpa ? appTheme.primary : colors.newUI.therapist.orange60
            }
            style={styles.headingIcon}
          />
          <Text style={styles.heading}>Datenübertragung</Text>
        </View>
        <Divider color={colors.lightGrey} width={2} />

        {!canWriteToEpa ? (
          <View style={styles.warningBanner}>
            <Icon
              name="exclamationCircle"
              size={24}
              color={colors.newUI.therapist.orange60}
              style={styles.headingIcon}
            />
            <Text style={[styles.textSmall, styles.warningBannerText]}>
              Sie haben Ihre Einwilligung zur Datenübertragung widerrufen. Es
              werden keine Übungsdaten mehr in Ihre ePA übertragen.
            </Text>
          </View>
        ) : null}

        <View style={[styles.cardSection, styles.rowBetween]}>
          <View style={styles.rowTextContainer}>
            <Text style={[styles.text, styles.textBold]}>
              Automatische Übertragung
            </Text>
            <Text style={styles.textSmall}>
              Übungsdaten werden täglich um 22:00 Uhr in die ePA übertragen
            </Text>
          </View>
          <Toggle
            value={userEpa?.autoUpload ?? false}
            onToggle={handleToggleAutoUpload}
            disabled={!canWriteToEpa}
          />
        </View>
        <Divider color={colors.lightGrey} width={2} />

        <View style={[styles.cardSection, styles.rowBetween]}>
          <View style={styles.rowTextContainer}>
            <Text style={[styles.text, styles.textBold]}>
              Einmalig manuell übertragen
            </Text>
            <Text style={styles.textSmall}>
              Überträgt alle bisherigen Ergebnisse sofort in Ihre ePA
            </Text>
          </View>
          <Button
            label="Jetzt übertragen"
            look="secondary"
            onPress={handleWriteToEpa}
            disabled={!canWriteToEpa}
          />
        </View>
      </View>

      <Modal
        isVisible={showEpaSuccess}
        onClose={() => setShowEpaSuccess(false)}
        title="Übertragung erfolgreich"
        confirmActionButton={
          <Button
            label="Schließen"
            look="primary"
            onPress={() => setShowEpaSuccess(false)}
          />
        }
      >
        <Text style={[styles.text, styles.textCenter]}>
          Ihre Übungsergebnisse wurden erfolgreich in Ihre ePA übertragen.
        </Text>
      </Modal>

      <Modal
        isVisible={showEpaError}
        onClose={() => setShowEpaError(false)}
        title="Übertragung fehlgeschlagen"
        cancelActionButton={
          <Button
            label="Abbrechen"
            look="ghost"
            onPress={() => setShowEpaError(false)}
          />
        }
        confirmActionButton={
          <Button
            label="Erneut versuchen"
            look="primary"
            onPress={() => {
              setShowEpaError(false);
              handleWriteToEpa();
            }}
          />
        }
      >
        <Text style={[styles.text, styles.textCenter]}>
          Die Daten konnten nicht in Ihre ePA übertragen werden. Bitte versuchen
          Sie es erneut oder kontaktieren Sie unseren Support, falls das Problem
          weiterhin besteht.
        </Text>
      </Modal>
    </>
  );
};
